import React from 'react';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'ghost' | 'primary' | 'secondary' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface NotionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  children?: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  ghost: `
    bg-transparent hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700/50
    text-[#37352F] dark:text-white
    border-none
  `,
  primary: `
    bg-[#37352F] hover:bg-[#2f2e2a]
    text-white
    border-none
  `,
  secondary: `
    bg-white dark:bg-gray-800
    hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700
    text-[#37352F] dark:text-white
    border border-[rgba(55,53,47,0.16)] dark:border-gray-700
  `,
  danger: `
    bg-red-500 hover:bg-red-600
    text-white
    border-none
  `
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs gap-1.5',
  md: 'px-3 py-2 text-sm gap-2',
  lg: 'px-4 py-2.5 text-base gap-2'
};

export function NotionButton({
  variant = 'ghost',
  size = 'md',
  icon,
  iconPosition = 'left',
  loading = false,
  disabled,
  children,
  className = '',
  ...props
}: NotionButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      className={`
        inline-flex items-center justify-center
        rounded-md font-medium
        transition-colors duration-150
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
      `}
      {...props}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          {icon && iconPosition === 'left' && <span className="flex-shrink-0">{icon}</span>}
          {children}
          {icon && iconPosition === 'right' && <span className="flex-shrink-0">{icon}</span>}
        </>
      )}
    </button>
  );
}

export default NotionButton;
